// Kitchen sink — a dev screen that renders every primitive in every state so
// the token system and components can be eyeballed at both viewports, in both
// themes, across all four accent packs. Not part of the operator app.

import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, IconButton } from '@/components/ui/Button';
import { FloorCard } from '@/components/andon/FloorCard';
import { alertEvents, andonAlert, andonDetail, causes, ownedAlert, threadMessages } from '@/lib/fixtures/andon';
import { keys } from '@/queries';
import { andonKeys } from '@/queries/andon';
import { AlertScreen } from '@/screens/alert/AlertScreen';
import { Chip } from '@/components/ui/Chip';
import { Banner } from '@/components/ui/Banner';
import { Icon, PodMark, Flag } from '@/components/ui/Icon';
import { NumberPad, PinDots } from '@/components/ui/NumberPad';
import { Sheet } from '@/components/ui/Sheet';
import { PlusFab } from '@/components/home/PlusFab';
import { PodStateChip } from '@/components/pod/PodParts';
import { TabBar } from '@/components/home/TabBar';
import { SideRail } from '@/components/shell/SideRail';
import type { Tab } from '@/store/slices/ui';
import type { OperatorHomeVM, PreRaiseQuestion, RoutingPreviewVM } from '@/api/andon/types';
import { AndonRaiseSheet } from '@/screens/phone/raise/AndonRaiseSheet';
import { OpenHereList } from '@/screens/phone/HomeScreen';
import type { AlertVM } from '@/api/types';
import {
  AvatarTile,
  Card,
  EmptyState,
  ReasonList,
  SectionLabel,
  SegmentedTabs,
  Skeleton,
} from '@/components/ui/misc';
import { useStore } from '@/store';
import { useT } from '@/i18n/useT';
import { ACCENT_NAMES, type AccentName, type ThemeMode } from '@/theme/palette';
import { LANGS, type Lang } from '@/i18n/strings';

const REASONS = [
  { id: 1, label: 'Material availability', dot: '#c0791f' },
  { id: 2, label: 'Equipment failure', dot: '#d64550', sub: 'Maintenance + Team Leader notified' },
  { id: 3, label: 'Setup & adjustments', dot: '#2f6bff' },
];

/**
 * A home payload and a routing preview the raise flow can be driven by with
 * no BFF behind it — the Andon app's own fixtures, trimmed. The identity
 * question is asked without a pod, materials asks trolley-or-part as well,
 * and the ranked half below answers a second late the way the server does.
 */
const IDENTITY: PreRaiseQuestion = {
  question: 'Which pod or project is this for?',
  options: ['MO/41240 · Type AA', 'MO/41241 · Type CS2', 'MO/41188 · Type AA'],
  kind: 'search',
  resolve: 'identity',
  searchIn: 'subjects',
  scan: true,
};
const TROLLEY: PreRaiseQuestion = {
  question: 'Is the whole trolley missing, or one part?',
  options: ['The whole trolley', 'One part'],
  kind: 'chips',
  resolve: 'picklist',
};
const SINK_HOME: OperatorHomeVM = {
  operator: { id: 1, name: 'Aileen Camoral' },
  station: { workcentreId: 1, workcentreName: 'Wall Tiling', flowlineId: 2, flowlineName: 'Flowline 2' },
  serverNow: Date.now(),
  mine: null,
  line: [],
  lineTotal: 0,
  shift: { raised: 0, medianAckMs: null, downtimeAvoidedMs: 0 },
  unreadCount: 0,
  ask: {
    withPod: { materials: [TROLLEY], quality: [], safety: [], upstream: [], equipment: [], labour: [], design: [], info: [], decision: [], other: [], test: [] },
    withoutPod: { materials: [IDENTITY, TROLLEY], quality: [IDENTITY], safety: [], upstream: [IDENTITY], equipment: [], labour: [], design: [], info: [], decision: [], other: [], test: [] },
    stopIn: { question: 'When will this stop the line?', options: ['20 min', '1 hr', '2 hrs'], kind: 'minutes', resolve: 'stopIn' },
    ranked: ['materials', 'quality', 'upstream', 'equipment'],
  },
  tiles: ['materials', 'quality', 'upstream', 'equipment', 'other', 'test'],
};
const SINK_PREVIEW: RoutingPreviewVM = {
  recipients: [
    { label: 'Stores', kind: 'tier1', reachable: true },
    { label: 'Dean Whitcombe', kind: 'manager', reachable: true },
    { label: 'Procurement', kind: 'tier1', reachable: false },
  ],
  routes: 'Stores + Procurement',
  broadcast: false,
};
/** One quality alert, as the shop floor's own read would send it, for the home rows. */
const SINK_QA: AlertVM = {
  id: 31866,
  qa: 'QA31866',
  title: 'Caulking · Gaps at the floor waste',
  moId: 41240,
  mo: 'MO/41240',
  stageId: 1,
  stage: 'New',
  stageDone: false,
  priority: '1',
  teamId: 3,
  team: 'Flowline Rectification',
  employeeId: 1,
  employeeName: 'Aileen Camoral',
  workcentreId: 1,
  workcentreName: 'Steel Cutting',
  categoryId: null,
  subcategoryId: null,
  category: 'Caulking › Gaps',
  description: '',
  pictureIds: [],
  beforePictureIds: [],
  afterPictureIds: [],
  requirePictures: false,
  andonAlertIds: [],
  createdAt: '2026-10-24 09:12:00',
  age: '1 h',
  mine: true,
  preventMoSignoff: true,
};

const sinkAsk = (input: { type: string }): Promise<PreRaiseQuestion[]> =>
  new Promise((resolve) =>
    setTimeout(
      () =>
        resolve(
          input.type === 'materials'
            ? [{ question: 'Which part is missing?', options: ['Door jamb', 'Hinge pack', 'Sealant'], kind: 'search', resolve: 'part', searchIn: 'parts', searchOptions: [{ label: 'Door jamb', detail: 'DJ-120' }, { label: 'Hinge pack', detail: 'HP-04' }] }]
            : input.type === 'quality'
              ? [{ question: 'What is the defect?', options: ['Caulking · Gaps', 'Tiling · Lippage', 'Door · Scratched'], kind: 'search', resolve: 'defect', searchIn: 'defects' }]
              : [],
        ),
      900,
    ),
  );

export function KitchenSink({ board }: { board: boolean }) {
  const t = useT();
  const s = useStore();
  const [pin, setPin] = useState('');
  const [sheet, setSheet] = useState<null | 'panel' | 'tall' | 'top'>(null);
  const [tab, setTab] = useState<'mo' | 'mine'>('mo');
  const [pending, setPending] = useState(false);
  // The bar and the rail with their badges lit, and the + with its dial, so
  // the five places can be eyeballed with no BFF behind them.
  const [barTab, setBarTab] = useState<Tab>('home');
  const [fabOpen, setFabOpen] = useState(false);

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 24, background: 'var(--bg)', minHeight: '100dvh' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 22 }}>
        <PodMark size={30} style={{ color: 'var(--ink)' }} />
        <div style={{ font: "700 20px 'Space Grotesk',sans-serif" }}>Kitchen sink</div>
        <div style={{ marginLeft: 'auto', font: '500 12px Manrope', color: 'var(--sub)' }}>
          {board ? 'board ≥1000px' : 'phone <1000px'} · {s.vw}px
        </div>
      </header>

      <SectionLabel>Theme</SectionLabel>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {(['light', 'dark', 'system'] as ThemeMode[]).map((m) => (
          <Chip key={m} kind="select" selected={s.userTheme === m} onClick={() => s.setTheme(m)}>
            {m === 'light' ? t.light : m === 'dark' ? t.dark : t.systemT}
          </Chip>
        ))}
      </div>

      <SectionLabel>{t.accentT}</SectionLabel>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {ACCENT_NAMES.map((a: AccentName) => (
          <Chip key={a} kind="select" selected={s.userAccent === a} onClick={() => s.setAccent(a)}>
            {a}
          </Chip>
        ))}
      </div>

      <SectionLabel>{t.language}</SectionLabel>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 }}>
        {LANGS.map((l: Lang) => (
          <Chip key={l} kind="select" selected={s.language === l} onClick={() => s.setLanguage(l)}>
            <Flag lang={l} /> {l}
          </Chip>
        ))}
      </div>

      <SectionLabel>Buttons</SectionLabel>
      <div style={{ display: 'grid', gap: 10, marginBottom: 10 }}>
        <Button variant="primary" icon={<Icon name="play" size={18} />}>
          {t.completeJobT}
        </Button>
        <Button variant="ink" icon={<Icon name="barcode" size={18} />}>
          {t.scanPod}
        </Button>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="surface" size="md">
            {t.back}
          </Button>
          <Button variant="soft" size="md">
            {t.documents}
          </Button>
          <Button variant="pos" size="md">
            {t.pass}
          </Button>
          <Button variant="danger" size="md">
            {t.fail}
          </Button>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="dangerGhost" size="md">
            {t.reportProblem}
          </Button>
          <Button disabled size="md">
            2 of 5 checks done
          </Button>
          <Button pending={pending} size="md" onClick={() => setPending(true)}>
            {pending ? t.saving : 'Tap → pending'}
          </Button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 24 }}>
        <IconButton label="Settings">
          <Icon name="gear" size={18} />
        </IconButton>
        <IconButton label="Search">
          <Icon name="search" size={18} />
        </IconButton>
        <IconButton label="Report">
          <Icon name="bell" size={18} />
        </IconButton>
        <IconButton label="Back">
          <Icon name="back" size={18} />
        </IconButton>
      </div>

      <SectionLabel>Chips</SectionLabel>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 24 }}>
        <Chip>RH</Chip>
        <Chip>CS2</Chip>
        <Chip>Astron</Chip>
        <Chip kind="status">In progress</Chip>
        <Chip kind="stage" tone="accent">
          Assigned
        </Chip>
        <Chip kind="stage" tone="warn">
          In progress
        </Chip>
        <Chip kind="stage" tone="pos">
          Resolved
        </Chip>
        <Chip kind="select" tone="danger" selected>
          {t.urgent}
        </Chip>
      </div>

      <SectionLabel>Pod state chips (every list row)</SectionLabel>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 24 }}>
        <PodStateChip state="pending" />
        <PodStateChip state="waiting" />
        <PodStateChip state="ready" />
        <PodStateChip state="progress" />
        <PodStateChip state="progress" started />
        <PodStateChip state="done" />
        <PodStateChip state="cancel" />
      </div>

      <SectionLabel>Banners</SectionLabel>
      <div style={{ display: 'grid', gap: 10, marginBottom: 24 }}>
        <Banner tone="danger" icon={<Icon name="hazard" size={18} />} action={{ label: t.clear, onClick: () => {} }}>
          {t.wcBlocked} · Equipment breakdown
        </Banner>
        <Banner tone="warn" action={{ label: t.resumeT, onClick: () => {} }}>
          {t.pausedT} · Material availability
        </Banner>
        <Banner tone="info" action={{ label: t.retry, onClick: () => {} }}>
          {t.couldntSave}
        </Banner>
      </div>

      <SectionLabel>Cards, avatars, states</SectionLabel>
      <div style={{ display: 'grid', gap: 12, marginBottom: 24 }}>
        <Card radius={22} style={{ padding: 16, display: 'flex', gap: 12, alignItems: 'center' }}>
          <AvatarTile name="Aileen Camoral" size={48} />
          <div>
            <div style={{ font: "700 16px 'Space Grotesk',sans-serif" }}>Aileen Camoral</div>
            <div style={{ font: '500 12px Manrope', color: 'var(--sub)' }}>Wall Tiling · Flowline 2</div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex' }}>
            {['AM', 'AS', 'AT'].map((n, i) => (
              <AvatarTile
                key={n}
                name={n}
                size={34}
                round
                background={['#0ea5a0', '#e0a21a', '#6366f1'][i]}
                style={{ marginLeft: i ? -6 : 0, border: '2px solid var(--surface)' }}
              />
            ))}
          </div>
        </Card>
        <Card tone="accent" radius={20} style={{ padding: 20 }}>
          <div style={{ font: '600 11px Manrope', letterSpacing: '.06em', opacity: 0.85 }}>
            FLOWLINE 2 · {t.todayT.toUpperCase()}
          </div>
          <div style={{ font: "700 34px 'Space Grotesk',sans-serif", marginTop: 6 }}>12 / 22 pods</div>
        </Card>
        <Skeleton />
        <Card tone="surface2" radius={13}>
          <EmptyState>{t.noOpenThisPod}</EmptyState>
        </Card>
      </div>

      <SectionLabel>Tabs & reasons</SectionLabel>
      <div style={{ display: 'grid', gap: 12, marginBottom: 24 }}>
        <SegmentedTabs
          label={t.qualityIssuesTitle}
          value={tab}
          onChange={setTab}
          options={[
            { value: 'mo', label: t.thisPodTab },
            { value: 'mine', label: t.raisedByMe },
          ]}
        />
        <ReasonList items={REASONS} onPick={() => {}} />
      </div>

      <SectionLabel>PIN</SectionLabel>
      <div style={{ display: 'grid', gap: 16, maxWidth: 320, marginBottom: 24 }}>
        <PinDots length={4} filled={pin.length} error={pin === '9999'} size={board ? 'board' : 'phone'} />
        <NumberPad
          size={board ? 'board' : 'phone'}
          onKey={(d) => setPin((p) => (p.length >= 4 ? p : p + d))}
          onDelete={() => setPin((p) => p.slice(0, -1))}
        />
      </div>

      <SectionLabel>Tab bar, the + and the rail (frame v2.12)</SectionLabel>
      <div style={{ display: 'grid', gap: 12, marginBottom: 24 }}>
        {/* The five-place bar with the floating + over it, in a frame the
            height of a phone's bottom third, so the disc, its 88px lift and
            the dial it opens can be eyeballed against the bar they sit on. */}
        <div style={{ position: 'relative', height: 420, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 18, overflow: 'hidden' }}>
          <TabBar tab={barTab} onTab={setBarTab} badges={{ alerts: 3, andon: 2 }} />
          <PlusFab open={fabOpen} onToggle={() => setFabOpen((v) => !v)} onChoose={(key) => { setFabOpen(false); s.showToast(`Would open ${key}`); }} />
        </div>
        <div style={{ display: 'flex', gap: 12, height: 360 }}>
          <SideRail tab={barTab} onTab={setBarTab} onRaise={() => s.showToast('Would open the raise kinds')} badges={{ alerts: 3, andon: 2 }} />
        </div>
      </div>

      {/* The raise sheet against a stubbed home and routing preview, so the
          whole of it — the severity row, the tiles, the question chips, the
          search box, the ranked half landing late, Skip and Don't know, the
          send's gate — can be walked through with no BFF. The draft is the
          real store's; the send only toasts. The sink's `ask` answers after
          900ms the way the server does, and the subjects/defects search
          boxes have no list behind them here, so they show their loading
          line. */}
      <SectionLabel>Raise sheet · andon preview (stubbed home)</SectionLabel>
      <div style={{ marginBottom: 24 }}>
        <SinkRaise />
      </div>

      {/* The thread on a stubbed andon, so the two faces, the verb row and
          every sheet behind it can be walked through with no BFF: the
          fixtures are seeded straight into the query cache under the keys the
          screen reads, and the session chips flip who is reading. The verbs
          and the composer make real calls, which fail against nothing and
          show their failure honestly — the send's rollback included. */}
      <SectionLabel>Andon thread · andon preview (stubbed alert)</SectionLabel>
      <SinkThread />

      {/* The operator home's "Open at this work centre" rows (frame L129-148)
          on fixtures: an unowned two-star past its target, Ben on one with
          an ETA, and a quality alert raised by this operator. */}
      <SectionLabel>Operator home · open at this work centre</SectionLabel>
      <div style={{ marginBottom: 24, maxWidth: 480 }}>
        <OpenHereList
          andons={[
            andonAlert({ sla: { ...andonAlert().sla, dueAt: Date.now() - 4 * 60_000 } }),
            ownedAlert({ id: 2, qa: 'AN00002', stars: 3, title: 'Biesse stopped on a jammed panel' }),
          ]}
          alerts={[SINK_QA]}
          now={Date.now()}
          operatorId={1}
          onOpenAndon={(id) => s.showToast(`Would open the thread on andon ${id}`)}
          onOpenQuality={(id) => s.showToast(`Would open quality alert ${id}`)}
        />
      </div>

      <SectionLabel>Floor cards · the leader's Andon tab</SectionLabel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24, maxWidth: 480 }}>
        <FloorCard alert={andonAlert({ raisedAt: Date.now() - 90_000, sla: { ...andonAlert().sla, dueAt: Date.now() + 210_000 } })} now={Date.now()} onOpen={() => s.showToast('Would open AN00001')} onTake={() => s.showToast('Would open the ETA sheet for AN00001')} />
        <FloorCard alert={ownedAlert({ id: 2, qa: 'AN00002', stars: 3, title: 'Biesse stopped on a jammed panel', raisedAt: Date.now() - 1_200_000 })} now={Date.now()} onOpen={() => s.showToast('Would open AN00002')} />
      </div>

      <SectionLabel>Sheets</SectionLabel>
      <div style={{ display: 'flex', gap: 10, marginBottom: 40 }}>
        <Button size="md" variant="surface" onClick={() => setSheet('panel')}>
          Open panel sheet
        </Button>
        <Button size="md" variant="surface" onClick={() => setSheet('tall')}>
          Open tall sheet
        </Button>
        <Button size="md" variant="surface" onClick={() => setSheet('top')}>
          Open top sheet
        </Button>
      </div>

      <Sheet
        open={sheet !== null}
        onClose={() => setSheet(null)}
        variant={sheet === 'tall' ? 'tall' : 'panel'}
        side={sheet === 'top' ? 'top' : 'bottom'}
        title={t.pausePod}
        footer={
          <Button variant="ink" onClick={() => setSheet(null)}>
            {t.back}
          </Button>
        }
      >
        <p style={{ font: '500 13px Manrope', color: 'var(--sub)', marginTop: 0 }}>{t.chooseReasonLogged}</p>
        <ReasonList items={REASONS} onPick={() => setSheet(null)} />
      </Sheet>
    </div>
  );
}

/** Who is reading the stubbed thread: a PIN session, an account, or a leader. */
type SinkSession = 'kiosk' | 'user' | 'leader';

/**
 * The alert screen on fixtures seeded into the query cache.
 *
 * Seeded rather than mocked: the screen reads `andonKeys.alert(1)` through
 * the same hook it always does, and with nothing answering the poll the
 * cached payload stays — react-query keeps data across a failed refetch —
 * so the thread draws exactly as it would on a BFF that had sent it. The
 * reference reads (causes, roster, responders, stations) are seeded too, so
 * the close sheet and the @ picker fill in.
 */
function SinkThread() {
  const qc = useQueryClient();
  const setConnMode = useStore((st) => st.setConnMode);
  const setUser = useStore((st) => st.setUser);
  const andonAppUrl = useStore((st) => st.andonAppUrl);
  const showToast = useStore((st) => st.showToast);
  const [session, setSession] = useState<SinkSession>('user');
  const [raiser, setRaiser] = useState(false);
  const [owned, setOwned] = useState<'nobody' | 'me' | 'ben'>('nobody');
  const [open, setOpen] = useState(true);

  useEffect(() => {
    setConnMode(session === 'kiosk' ? 'kiosk' : 'user');
    setUser(session === 'kiosk' ? null : 7, session === 'leader', andonAppUrl);
  }, [session, setConnMode, setUser, andonAppUrl]);

  useEffect(() => {
    // Raised a minute ago, due in two, so the clock and the "min over" chip
    // read as they would on the floor rather than off the fixture's 2023 stamp.
    const now = Date.now();
    const fresh = { raisedAt: now - 60_000, sla: { ...andonAlert().sla, dueAt: now + 120_000 } };
    const base =
      owned === 'nobody'
        ? andonAlert(fresh)
        : ownedAlert({ ...fresh, mine: owned === 'me', owner: { name: owned === 'me' ? 'You Yourself' : 'Ben Curtis', role: 'Stores', unowned: false, uid: owned === 'me' ? 7 : 8 } });
    const alert = andonDetail({ ...base, raisedByMe: raiser, qualityAlertId: 31866 });
    qc.setQueryData(andonKeys.alert(1), { alert, thread: threadMessages(), events: alertEvents() });
    qc.setQueryData(andonKeys.causes, causes());
    qc.setQueryData(andonKeys.roster, [
      { login: 'ben', name: 'Ben Curtis', uid: 8, department: 'Stores', onShift: true },
      { login: 'dean', name: 'Dean Whitcombe', uid: 9, department: 'Production', onShift: null },
      { login: 'priya', name: 'Priya Kaur', uid: 10, department: 'Procurement', onShift: true },
    ]);
    qc.setQueryData(andonKeys.responders, [
      { login: 'ben', name: 'Ben Curtis', uid: 8 },
      { login: 'dean', name: 'Dean Whitcombe', uid: 9 },
    ]);
    qc.setQueryData(andonKeys.stations, {
      workcentres: [
        { id: 1, name: 'Steel Cutting', code: 'SC', isFlowline: false },
        { id: 2, name: 'Frame Weld', code: 'FW', isFlowline: true },
      ],
      flowlines: [],
    });
    qc.setQueryData(andonKeys.floor('open'), { serverNow: Date.now(), alerts: [alert, ownedAlert({ id: 2, qa: 'AN00002' })], closedHours: 24, omitted: 0 });
    // The shop floor's own work-centre list, which the leader's "Go to
    // <work centre>" on the thread resolves the andon's station against.
    qc.setQueryData(keys.workcentres, [
      { id: 1, name: 'Steel Cutting', code: 'SC', sequence: 10, isEol: false, isFlowline: false, blocked: false, departmentId: null, departmentName: 'Welding', calendarId: null },
    ]);
  }, [qc, raiser, owned]);

  const chip = <T extends string>(value: T, current: T, label: string, set: (v: T) => void) => (
    <Chip key={value} kind="select" selected={current === value} onClick={() => set(value)}>
      {label}
    </Chip>
  );

  return (
    <div style={{ marginBottom: 24, maxWidth: 480 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        {chip('kiosk', session, 'PIN session', setSession)}
        {chip('user', session, 'Personal account', setSession)}
        {chip('leader', session, 'Leader', setSession)}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        {chip('nobody', owned, 'Nobody has it', setOwned)}
        {chip('ben', owned, 'Ben has it', setOwned)}
        {chip('me', owned, 'I have it', setOwned)}
        <Chip kind="select" selected={raiser} onClick={() => setRaiser((v) => !v)}>
          I raised it
        </Chip>
      </div>
      {open ? (
        <div style={{ height: 640, display: 'flex', flexDirection: 'column', background: 'var(--bg)', borderRadius: 18, border: '1px solid var(--line)', overflow: 'hidden' }}>
          <AlertScreen alertId={1} onClose={() => setOpen(false)} tablet={false} onGoToStation={(_id, name) => showToast(`Would go to ${name}`)} />
        </div>
      ) : (
        <Button size="md" variant="surface" onClick={() => setOpen(true)}>
          Open the thread again
        </Button>
      )}
    </div>
  );
}

/** The raise sheet on the store's draft, with the fixtures above in place of a BFF. */
function SinkRaise() {
  const stars = useStore((s) => s.draftStars);
  const startRaise = useStore((s) => s.startRaise);
  const showToast = useStore((s) => s.showToast);
  const workcentreId = useStore((s) => s.workcentreId);
  const setStation = useStore((s) => s.setStation);
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);

  return (
    <>
      <Button
        size="md"
        variant="danger"
        onClick={() => {
          // The sheet refuses to raise from a device with no station; the
          // sink has none, so it points the device at the stub's.
          if (workcentreId == null) setStation({ id: 1, name: 'Wall Tiling' }, { id: 2, name: 'Flowline 2' });
          startRaise();
          setOpen(true);
        }}
      >
        Open the raise sheet
      </Button>
      <AndonRaiseSheet
        open={open}
        onClose={() => setOpen(false)}
        home={SINK_HOME}
        homePending={false}
        preview={SINK_PREVIEW}
        ask={sinkAsk}
        raise={{
          isPending: pending,
          mutate: (input) => {
            setPending(true);
            setTimeout(() => {
              setPending(false);
              showToast(`Would raise ★${stars} ${input.type} with ${input.answers.length} answers, ${input.attachments.length} photos`);
              startRaise();
              setOpen(false);
            }, 800);
          },
        }}
        rules={{ ackMinutes: { '1': 30, '2': 10, '3': 5 } }}
        onPickStation={() => showToast('Would open the station picker')}
        onSignOut={() => showToast('Would sign out')}
      />
    </>
  );
}
